import React, { useEffect, useState } from 'react';
import { logAudit } from '../services/demoExtrasService';

const ROLE_KEY = 'nexus_demo_role';
export const ROLES = [
  { id: 'admin',    label: 'Demo Admin',     user: 'demo-admin',     color: '#16a34a', icon: '⚙' },
  { id: 'standard', label: 'Standard User',  user: 'demo-standard',  color: '#2563eb', icon: '👤' },
  { id: 'readonly', label: 'Read-Only User', user: 'demo-readonly',  color: '#64748b', icon: '👁' }
];

export const ROLE_PERMS = {
  admin:    ['read', 'write', 'export', 'import', 'annotate', 'onboard', 'admin'],
  standard: ['read', 'write', 'export', 'import', 'annotate'],
  readonly: ['read']
};

// Some layers/groups are restricted by role to make the contrast obvious in demos.
export const RESTRICTED_LAYERS_BY_ROLE = {
  readonly: ['transmission_mains', 'pipe_risk', 'work_orders_open', 'crews', 'dispersion_plumes'],
  standard: ['pipe_risk'] // standard cannot see capital planning data
};

export function getCurrentRole() {
  const id = (typeof window !== 'undefined' && window.localStorage.getItem(ROLE_KEY)) || 'admin';
  return ROLES.find((r) => r.id === id) || ROLES[0];
}

export function getCurrentUser() {
  return getCurrentRole().user;
}

export function canDo(action, role) {
  const r = role || getCurrentRole().id;
  return (ROLE_PERMS[r] || []).includes(action);
}

export function isLayerVisibleForRole(layerId, role) {
  const r = role || getCurrentRole().id;
  const blocked = RESTRICTED_LAYERS_BY_ROLE[r] || [];
  return !blocked.includes(layerId);
}

/** Reactive hook: re-renders the consumer when the role changes. */
export function useCurrentRole() {
  const [role, setRole] = useState(getCurrentRole());
  useEffect(() => {
    const onChange = () => setRole(getCurrentRole());
    window.addEventListener('nexus-role-change', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('nexus-role-change', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);
  return role;
}

export default function RoleSwitcher({ onChange }) {
  const [role, setRole] = useState(getCurrentRole().id);

  useEffect(() => {
    const onStorage = () => setRole(getCurrentRole().id);
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const handle = (e) => {
    const next = e.target.value;
    setRole(next);
    window.localStorage.setItem(ROLE_KEY, next);
    const role = ROLES.find((r) => r.id === next);
    logAudit({ user: role.user, role: next, action: 'ROLE_SWITCH', detail: `Switched to ${role.label}` });
    if (onChange) onChange(next);
    // Fire a custom event so other components can react without a full reload
    window.dispatchEvent(new CustomEvent('nexus-role-change', { detail: next }));
  };

  const current = ROLES.find((r) => r.id === role) || ROLES[0];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} title="Demo: switch user role to show RBAC">
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: current.color, display: 'inline-block' }} />
      <select
        value={role}
        onChange={handle}
        style={{
          background: 'rgba(15,23,42,0.6)',
          color: '#e2e8f0',
          border: '1px solid rgba(148,163,184,0.3)',
          borderRadius: 6,
          padding: '4px 8px',
          fontSize: 12,
          cursor: 'pointer'
        }}
      >
        {ROLES.map((r) => (
          <option key={r.id} value={r.id} style={{ color: '#0f172a' }}>{r.label}</option>
        ))}
      </select>
    </div>
  );
}
